/**
* Composition for Projects (My Products) logic
*/
export default function (options = { key: 'full' }) {
  const { confGlobal } = useConfig()
  const { query } = useRoute()
  const { $member, $i18n: i18n } = useNuxtApp()
  const { requestHeaders } = useApi()
  const toast = useToast()

  let tick = ''

  /**
  * Default Limit
  */
  const defaultLimit = 7

  /**
  * Current Projects Params
  */
  const paramsProjects = useState(`paramsProjects_${options.key}`, () => false)

  /**
  * URL Params String
  */
  const urlParamsString = computed({
    get () {
      const params = ref({})

      if (paramsProjects.value.page) {
        params.value.page = paramsProjects.value.page
      } else {
        params.value.page = parseInt(query.page) > 0 ? query.page * 1 : 1
      }

      if (paramsProjects.value.limit) {
        params.value.limit = paramsProjects.value.limit
      } else {
        params.value.limit = defaultLimit
      }

      if (paramsProjects.value.filters) {
        params.value['filters[isSlp]'] = paramsProjects.value.filters.isSlp
        params.value['filters[isShort]'] = paramsProjects.value.filters.isShort
        params.value['filters[selected_ids]'] = paramsProjects.value.filters.selected_ids
        params.value['filters[without_specifications]'] = paramsProjects.value.filters.without_specifications
      }

      let paramsString = new URLSearchParams(params.value).toString()
      if (paramsString) {
        paramsString = '?' + paramsString
      }

      return paramsString
    }
  })

  /**
  * Project
  */
  const project = useState('customer_project', () => false)

  /**
  * Projects
  */
  const projects = useState(`customer_projects_${options.key}`, () => false)

  /**
  * Get Project by ID
  */
  const getProject = (id) => {
    try {
      return $fetch(confGlobal.apiUrl + `/customers/projects/${id}`, {
        method: 'GET',
        headers: requestHeaders()
      }).then(async (res) => {
        project.value = await res

        return true
      }).catch((err) => {
        console.log(err)

        toast.add({
          severity: 'error',
          detail: i18n?.t('serverError'),
          life: 5000
        })
        return true
      })
    } catch (err) {
      console.log(err)
    }
  }

  /**
  * Get Projects List
  */
  const getProjects = () => {
    try {
      return useAsyncData(`projects_${$member.user.value?.id}_${urlParamsString.value}_${options.key}`, async () => {
        // Request
        return await $fetch(confGlobal.apiUrl + `/customers/projects${urlParamsString.value}`, {
          method: 'GET',
          headers: requestHeaders()
        }).then(async (res) => {
          const projectsData = await res

          if (!projectsData.data) {
            projects.value = {}

            projects.value.error = {
              code: 500
            }
            return true
          }

          if (typeof projects.value !== 'object') {
            projects.value = {
              data: {},
              _meta: {}
            }
          }

          if (!projects.value.data.projects) {
            projects.value.data.projects = []
          }

          projectsData.data.projects.forEach((project) => {
            if (projects.value.data.projects.find(n => n.id === project.id)) {
              const index = projects.value.data.projects.findIndex(n => n.id === project.id)

              projects.value.data.projects[index] = project
            } else {
              projects.value.data.projects.push(project)
            }
          })

          projects.value._meta = projectsData?._meta

          return {
            data: {
              projects: projects.value.data.projects
            },
            _meta: projectsData?._meta
          }
        }).catch((err) => {
          console.log(err)

          toast.add({
            severity: 'error',
            detail: i18n?.t('serverError'),
            life: 5000
          })
          return true
        })
      })
    } catch (err) {
      console.log(err)
    }
  }

  /**
  * Edit Project
  */
  const editProject = (paramsData = {}) => {
    try {
      clearTimeout(tick)
      tick = setTimeout(() => {
        $fetch(confGlobal.apiUrl + '/customers/projects/' + paramsData.id, {
          method: 'PUT',
          body: {
            name: paramsData.name
          },
          headers: requestHeaders()
        }).then(() => {
          toast.add({
            severity: 'success',
            detail: i18n?.t('projectSuccessfullyEdited'),
            life: 5000
          })
        }).catch((err) => {
          console.log(err)

          toast.add({
            severity: 'error',
            detail: i18n?.t('serverError'),
            life: 5000
          })
        })
      }, 500)
    } catch (err) {
      console.log(err)
    }
  }

  /**
  * Delete Project
  */
  const deleteProject = (formData) => {
    try {
      return $fetch(confGlobal.apiUrl + '/customers/projects/' + formData.id, {
        method: 'DELETE',
        headers: requestHeaders()
      }).then(async (res) => {
        const projectData = await res

        toast.add({
          severity: 'success',
          detail: i18n?.t('projectSuccessfullyDeleted'),
          life: 5000
        })

        return projectData
      }).catch((err) => {
        if (err.data && err?.data?.error && err?.data?.error?.description) {
          toast.add({
            severity: 'error',
            detail: err?.data?.error?.description,
            life: 5000
          })
        } else {
          toast.add({
            severity: 'error',
            detail: i18n?.t('serverError'),
            life: 5000
          })
        }

        return true
      })
    } catch (err) {
      console.log(err)
    }
  }

  /**
  * Get Project Price
  */
  const getProjectPrice = (paramsData = {}) => {
    try {
      const project = projects.value.data.projects.find(n => n.id === paramsData.id)
      if (!project) {
        return false
      }

      clearTimeout(tick)
      tick = setTimeout(async () => {
        // Get Parameters
        const params = {}
        params.product_id = paramsData.product_id ? paramsData.product_id : 0
        params.delivery_time = paramsData.production_time
        params.quantity = paramsData.quantity ? paramsData.quantity : 1
        params.selected_specification_sku = paramsData.selected_specification_sku ? paramsData.selected_specification_sku : null

        // Params
        let paramsString = new URLSearchParams(params).toString()
        if (paramsString) {
          paramsString = '?' + paramsString
        }

        // Fetch
        return await $fetch(confGlobal.apiUrl + '/customers/projects/' + paramsData.id + '/price' + paramsString, {
          method: 'GET',
          headers: requestHeaders()
        }).then(async (res) => {
          // Update price
          project.total = await res.price
        }).catch((err) => {
          console.log(err)

          toast.add({
            severity: 'error',
            detail: i18n?.t('serverError'),
            life: 5000
          })
        })
      }, 500)
    } catch (err) {
      console.log(err)
    }
  }

  /**
  * Add to Cart
  */
  const addProjectToCart = (paramsData = {}) => {
    try {
      return $fetch(confGlobal.apiUrl + '/customers/projects/' + paramsData.id + '/add-to-cart', {
        method: 'POST',
        body: paramsData,
        headers: requestHeaders()
      }).then(async (res) => {
        toast.add({
          severity: 'success',
          detail: i18n?.t('projectSuccessfullyAddedToCart'),
          life: 5000
        })

        $member.getMember()

        return await res
      }).catch((err) => {
        if (err.data && err?.data?.error && err?.data?.error?.description) {
          const errors = []

          if (typeof err?.data?.error?.description === 'object') {
            for (const i in err?.data?.error?.description) {
              if (typeof err?.data?.error?.description[i] === 'object') {
                for (const m in err?.data?.error?.description[i]) {
                  errors.push(err?.data?.error?.description[i][m])
                }
              } else {
                errors.push(err?.data?.error?.description[i])
              }
            }
          } else {
            errors.push(err?.data?.error?.description)
          }

          toast.add({
            severity: 'error',
            detail: errors.join('\n'),
            life: 5000
          })

          return true
        }

        toast.add({
          severity: 'error',
          detail: i18n?.t('serverError'),
          life: 5000
        })
        return true
      })
    } catch (err) {
      console.log(err)
    }
  }

  /**
  * Return Object
  */
  return {
    defaultLimit,
    paramsProjects,

    project,
    projects,

    getProject,
    getProjects,
    editProject,
    deleteProject,

    getProjectPrice,
    addProjectToCart
  }
}
